import { DateRange } from '@blueprintjs/datetime'

/*
Note, these don't provide a good mechanism for performing calculations explicitly in the organization's timezone.
The browser timezone will basically always equal the org's timezone, for customers.
Upright employees will often need to adjust their computer's / browser's timezone to match the customer for some things to line up perfectly.
https://uprightlabs.slack.com/archives/GDP2PUH70/p1659986129506829
 */

/**
 * Builds a date range scoped to timezone and beginning and end of days
 */
export function buildDateRangeByDays(daysAgo: number): DateRange {
  const NOW = new Date()
  const AGO = new Date(new Date().setDate(NOW.getDate() - daysAgo))
  AGO.setHours(0, 0, 0, 0)
  NOW.setHours(23, 59, 59, 999)

  return [AGO, NOW]
}

/**
 * True if a given date is non-null and valid, false otherwise.
 */
export function isValidDate(d: Date) {
  return d && d instanceof Date && !Number.isNaN(d.valueOf())
}

/**
 * Get just the date component in partial ISO format
 * Useful with date pickers when the backend is master of timezone
 * E.g. reports - User selects a date on a calendar and a timezone.
 * This helps prevent confusions between local timezone, organization timezone, and selected export timezone
 */
export function buildDateOnlyISOString(date: Date): string {
  if (!isValidDate(date)) return null

  const year = date.getFullYear()
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')

  return `${year}-${month}-${day}`
}

/**
 * Get the number of minutes since the all-mighty EPOCH
 * This helps reason about finding the difference between two dates that are several days or weeks apart
 * Where milliseconds are irrelevant.
 */
const MILLISECONDS_PER_MINUTE = 60000
export function toMinutesSince1970(date?: Date) {
  if (!date) return null

  return date.valueOf() / MILLISECONDS_PER_MINUTE
}

/**
 * Use a date-only iso string to get a Date object in the current timezone on that date.
 *
 * Example input:
 * YYYY-MM-DD, example: 2019-05-06
 *
 * Month number is automatically decremented for input to the Date contructor (which is zero-indexed for month)
 */
export function buildDateFromDateOnlyISOString(dateString: string) {
  if (!dateString) return null

  const parts = dateString.split('-').map(d => parseInt(d))
  if (parts.filter(d => d).length !== 3) return null
  const [year, month, day] = parts

  // month is decremented because Date indexes from 0, safety set minimum to 0
  return new Date(year, Math.max(0, month - 1), day)
}

/** Offset a given date by day, month, year or any combination of intervals
 *
 * @param intervals a keyed object of date component offsets
 * @param originDate the date to transform, defaults to now
 * @returns a transformed copy of `originDate`
 */
export const offsetLocalDate = (
  intervals: {
    year?: number
    month?: number
    day?: number
  },
  originDate: Date = null
): Date => {
  const date = originDate ? new Date(originDate) : new Date()
  date.setFullYear(
    date.getFullYear() + (intervals.year || 0),
    date.getMonth() + (intervals.month || 0),
    date.getDate() + (intervals.day || 0)
  )
  return date
}

/** Offset a given date by hour, minute, second, millisecond or any combination of intervals
 *
 * @param intervals a keyed object of time component offsets
 * @param originDate the date to transform, defaults to now
 * @returns a transformed copy of `originDate`
 */
export const offsetLocalTime = (
  intervals: {
    hour?: number
    minute?: number
    second?: number
    millisecond?: number
  },
  originDate: Date = null
): Date => {
  const date = originDate ? new Date(originDate) : new Date()
  date.setHours(
    date.getHours() + (intervals.hour || 0),
    date.getMinutes() + (intervals.minute || 0),
    date.getSeconds() + (intervals.second || 0),
    date.getMilliseconds() + (intervals.millisecond || 0)
  )
  return date
}

/** Sets the given date to the start of the current day, with millisecond precision
 *
 * @param originDate the date to transform, defaults to now
 * @returns a transformed copy of `originDate`
 */
export const startOfLocalDay = (originDate: Date = null): Date => {
  const date = offsetLocalDate({ day: -1 }, originDate)
  date.setHours(24, 0, 0, 0)
  return date
}

/** Sets the given date to the end of the current day, with millisecond precision
 *
 * @param originDate: Date - the date to transform, defaults to now
 * @returns a transformed copy of `originDate`
 */
export const endOfLocalDay = (originDate: Date = null): Date => {
  const date = offsetLocalDate({ day: 1 }, originDate)
  date.setHours(0, 0, 0, -1)
  return date
}

/** Use this function to convert user-friendly report times to Javascript date objects.
 * `new Date(dateString)` cannot reliably parse the format we use on our report rows, which lead to some crashes on mobile devices (due to browser support differences)
 * Luckily, the dates are easy enough to parse on our own, so we don't need to rely on any heavy black-box frameworks here.
 *
 * This method will try to use the built in date constructor, so any valid date string should convert into a date (e.g. iso8601)
 *
 * @param dateString a string that matches format MM/DD/YYYY HH:MI:SS (Reports::Base::DATE_FORMAT_PG from /lib/reports/base.rb)
 * @returns a date object representing the given date and time (locally)
 */
export const parseReportDate = (dateString: string): Date => {
  const naiveAttempt = new Date(dateString)
  if (isValidDate(naiveAttempt)) return naiveAttempt

  const parts = dateString.split(' ')

  if (parts && parts.length !== 2) return null

  const isValueTruthyOrZero = (v: any) => v || v === 0

  const dateParts = parts[0]
    .split('-')
    .map(v => parseInt(v))
    .filter(isValueTruthyOrZero)
    .reverse()
  const timeParts = parts[1]
    .split(':')
    .map(v => parseInt(v))
    .filter(isValueTruthyOrZero)

  if (!dateParts || !timeParts || dateParts.length !== 3 || timeParts.length !== 3) return null

  return new Date(dateParts[2], Math.max(0, dateParts[1] - 1), dateParts[0], timeParts[0], timeParts[1], timeParts[2])
}
